const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

type ParticipantType = 'owner' | 'partner' | 'admin' | 'other';
type ReportFormat = 'pdf' | 'xlsx';

export interface OwnershipParticipationDTO {
  id: string;
  ownerEmail: string;
  tenantId: string;
  consultorioId: number | null;
  participantName: string;
  participantEmail: string | null;
  participantType: ParticipantType;
  percentage: number;
  validFrom: string;
  validTo: string | null;
  active: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface OwnershipParticipationPayload {
  tenantId: string;
  consultorioId?: number | null;
  participantName: string;
  participantEmail?: string | null;
  participantType: ParticipantType;
  percentage: number;
  validFrom: string;
  validTo?: string | null;
  active?: boolean;
}

export interface OwnershipPreviewParticipant extends OwnershipParticipationDTO {
  amount: number;
}

export interface OwnershipIncomeDetailDTO {
  id: string;
  date: string;
  consultorioId: number | null;
  consultorioName: string | null;
  patientName: string | null;
  patientDocument: string | null;
  professionalName: string | null;
  concept: string | null;
  paymentMethod: string | null;
  amount: number;
}

export interface OwnershipProfessionalCommissionDetailDTO {
  professionalId: number | null;
  professionalName: string | null;
  consultorioId: number | null;
  consultorioName: string | null;
  grossIncome: number;
  commissionPercentage: number;
  commissionAmount: number;
  movements: number;
}

export interface OwnershipPreviewDTO {
  tenantId: string;
  consultorioId: number | null;
  period: string;
  periodStart: string;
  periodEnd: string;
  totalCollected: number;
  professionalCommission: number;
  clinicNet: number;
  configuredPercentage: number;
  distributed: number;
  undistributed: number;
  participants: OwnershipPreviewParticipant[];
  incomeDetails: OwnershipIncomeDetailDTO[];
  professionalCommissionDetails: OwnershipProfessionalCommissionDetailDTO[];
}

async function requestJson<T>(token: string | null, path: string, init?: RequestInit): Promise<T> {
  if (!token) throw new Error('unauthorized');
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new Error('network_error');
  }

  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new Error(payload?.message ?? payload?.error ?? 'request_failed');
  }

  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

export function listOwnership(token: string | null, params: { tenantId?: string; active?: boolean } = {}): Promise<OwnershipParticipationDTO[]> {
  const query = new URLSearchParams();
  if (params.tenantId) query.set('tenantId', params.tenantId);
  if (params.active !== undefined) query.set('active', String(params.active));
  const suffix = query.toString() ? `?${query.toString()}` : '';
  return requestJson<OwnershipParticipationDTO[]>(token, `/ownership${suffix}`);
}

export function createOwnership(token: string | null, payload: OwnershipParticipationPayload): Promise<OwnershipParticipationDTO> {
  return requestJson<OwnershipParticipationDTO>(token, '/ownership', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function ensureOwnershipDefaults(token: string | null, tenantId: string): Promise<{ created: boolean; rows: OwnershipParticipationDTO[] }> {
  return requestJson<{ created: boolean; rows: OwnershipParticipationDTO[] }>(token, '/ownership/defaults', {
    method: 'POST',
    body: JSON.stringify({ tenantId }),
  });
}

export function updateOwnership(token: string | null, id: string, payload: OwnershipParticipationPayload): Promise<OwnershipParticipationDTO> {
  return requestJson<OwnershipParticipationDTO>(token, `/ownership/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
}

export function setOwnershipStatus(token: string | null, id: string, active: boolean): Promise<OwnershipParticipationDTO> {
  return requestJson<OwnershipParticipationDTO>(token, `/ownership/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });
}

export function getOwnershipPreview(
  token: string | null,
  params: { tenantId: string; year: number; month: number; months?: number; consultorioId?: number | null }
): Promise<OwnershipPreviewDTO> {
  const query = new URLSearchParams({
    tenantId: params.tenantId,
    year: String(params.year),
    month: String(params.month),
    months: String(params.months ?? 1),
  });
  if (params.consultorioId) query.set('consultorioId', String(params.consultorioId));
  return requestJson<OwnershipPreviewDTO>(token, `/ownership/preview?${query.toString()}`);
}

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const match = /filename="?([^";]+)"?/.exec(header);
  return match?.[1]?.trim() ?? fallback;
}

async function downloadBinary(token: string | null, path: string, fallbackName: string): Promise<void> {
  if (!token) throw new Error('unauthorized');
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null) as { error?: string; message?: string } | null;
    throw new Error(payload?.message ?? payload?.error ?? 'request_failed');
  }

  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('content-disposition'), fallbackName);
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function downloadOwnershipReport(
  token: string | null,
  params: { tenantId: string; year: number; month: number; months?: number; consultorioId?: number | null },
  format: ReportFormat
): Promise<void> {
  const query = new URLSearchParams({
    tenantId: params.tenantId,
    year: String(params.year),
    month: String(params.month),
    months: String(params.months ?? 1),
    format,
  });
  if (params.consultorioId) query.set('consultorioId', String(params.consultorioId));
  return downloadBinary(token, `/ownership/preview/export?${query.toString()}`, `participacion-socios.${format}`);
}
